import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnInit,
  ViewChild,
} from "@angular/core";
import {
  Chart,
  ChartConfiguration,
  ChartData,
  ChartDataset, // Replaces ChartDataSets
  ChartOptions,
  ChartType,
} from "chart.js";
import {
  ChartJSComponent,
  CptWidgetInput,
  EfieldsWidgetInput,
  GeneratedEfieldsWidgetInput,
  UserChartWidgetInput,
} from "../../types/types";
import { ExplorerService } from "../../services/explorer.service";
import {
  ChartBackgroundColors,
  ChartBorderColors,
} from "../../types/constants";
import { BaseChartDirective } from 'ng2-charts';
import { EFieldsTrial } from 'src/app/shared/models/eFieldTrial';

const FIXED_WIDTH = 500;

@Component({
    selector: "app-widget",
    templateUrl: "./chart-widget.component.html",
    styleUrls: ["./widget.component.scss"],
    standalone: false
})
export class ChartWidgetComponent implements OnInit, AfterViewInit, ChartJSComponent {
  @ViewChild(BaseChartDirective, { static: false }) chart?: BaseChartDirective;
  @ViewChild("chart") chartContainer?: ElementRef;
  @ViewChild("resizeContainer") resizeContainer?: ElementRef;
  @Input() widgetInput!:
    | CptWidgetInput
    | UserChartWidgetInput
    | EfieldsWidgetInput
    | GeneratedEfieldsWidgetInput;

  // Use generic types for broader compatibility
  data: ChartDataset[] = []; 
  options!: ChartOptions;
  labels: string[] = [];
  type: ChartType = 'bar'; // Default type
  activeScopes: string[] = [];
  efieldsInput: boolean = false;
  displayChart: boolean = true;
  multi: boolean = false;
  private _displayImage: string = '';

  get displayImage(): string {
    return this._displayImage;
  }

  get myChart(): BaseChartDirective | undefined {
    return this.chart;
  }

  setDisplayImage() {
    if (!this.myChart) return;
    this._displayImage = this.myChart.toBase64Image();
  }

  updateChartElement(width: number, height: number): void {
    this.displayChart = width > 250;
    if (this.displayChart) return;
    let sizeRatio = height / width;
    width = 400;
    height = width * sizeRatio;
    this.displayChart = false;
    setTimeout(() => {
      let element = this.resizeContainer?.nativeElement;
      if (!element || !this.myChart) return;
      element.style.width = `${width}px`;
      element.style.height = `${height}px`;
      this.myChart.update();
    }, 100);
  }

  constructor(private _explorer: ExplorerService) {}

  ngOnInit(): void {
    let source = this.widgetInput.selection.source;
    if (source == 'cpt_census' || source == 'cpt_nass') {
      this.activeScopes = this.cptWidgetInput.scopes.map(s => s);
    }
  }

  ngAfterViewInit(): void {
    this.initalizeChart();
  }

  updateScope(newScope: string) {
    console.log(newScope);
    this.activateScopes([newScope]);
  }

  activateScopes(scopes: string[]): void {
    this.activeScopes = scopes;
    this.initalizeChart();
  }

  getImage(): string {
    return this.myChart?.toBase64Image() || '';
  }

  get cptWidgetInput(): CptWidgetInput {
    return this.widgetInput as CptWidgetInput;
  }

  private getDefaultConfig(): ChartConfiguration {
    const config: ChartConfiguration = {
      type: 'bar', // Default, will be overridden
      data: {
        labels: [],
        datasets: [
          {
            data: [],
            label: "",
          },
        ],
      },
      options: {
        animation: {
          onComplete: () => {
            this.setDisplayImage();
          }
        },
        devicePixelRatio: 2.5,
        plugins: {
          legend: {
            display: true,
            labels: {
              font: {
                family: "'Arial', sans-serif"
              },
              color: '#333'
            }
          }
        },
        scales: {
          x: {
            ticks: {
              font: {
                size: 10,
                weight: 'bold'
              },
              color: '#333',
            },
          },
          y: {
            beginAtZero: false,
            ticks: {
              font: {
                size: 10,
                weight: 'bold'
              },
              color: '#333',
            },
          },
        },
        responsive: true,
        maintainAspectRatio: false
      },
    };
    return config;
  }

  private getEfieldsConfig(input: EfieldsWidgetInput): ChartConfiguration {
    // Note: EfieldsWidgetInput types might need casting if they still use old structures
    let config: ChartConfiguration = {
      type: input.type as ChartType,
      data: {
        datasets: input.datasets as ChartDataset[],
        labels: input.labels,
      },
      options: input.options as ChartOptions,
    };
    
    // Ensure options object exists
    config.options = config.options || {};
    config.options.devicePixelRatio = 2.5;
    
    // Set Plugins
    config.options.plugins = config.options.plugins || {};
    
    config.options.plugins.legend = {
      labels: {
        font: { family: "'Arial', sans-serif" },
        color: "#333"
      }
    };
    
    config.options.plugins.title = {
        font: { family: "'Georgia', serif" },
        color: '#333'
    };

    config.options.scales = {
      x: {
        ticks: {
          font: { size: 10, weight: 'bold' },
          color: '#333',
        },
      },
      y: {
        beginAtZero: false,
        ticks: {
          font: { size: 10, weight: 'bold' },
          color: '#333',
        },
      },
    };

    return config;
  }

  private sortLineData(
    input: CptWidgetInput,
    scope: string
  ): { year: number; value: number | null }[] {
    let yearValues = [];
    for (let i = 0; i < input.cptWidget.years.length; i++) {
      let year = parseInt(input.cptWidget.years[i]["year"]);
      console.log(input.variables[i]);
      let rawValue = input.variables[i].data[scope];
      let value = rawValue ? parseFloat(rawValue.replace(",", "")) : -1;
      
      if (!value || value == -1) {
        // value = null; // In strict TS, might need to handle null differently
      }
      yearValues.push({
        year: year,
        value: (value === -1 || !value) ? null : value
      });
    }
    console.log(yearValues);
    return yearValues.sort((a, b) => {
      let val = a.year > b.year ? 1 : b.year > a.year ? -1 : 0;
      return val;
    });
  }

  private getCptConfig(input: CptWidgetInput): ChartConfiguration {
    let config = this.getDefaultConfig();

    // Handle horizontalBar migration
    if ((input.selection.type as string) === 'horizontalBar') {
        config.type = 'bar';
        if (config.options) {
            config.options.indexAxis = 'y';
        }
    } else {
        config.type = input.selection.type as ChartType;
    }

    if (!config.options) config.options = {};
    if (!config.options.plugins) config.options.plugins = {};

    config.options.plugins.title = {
      display: true,
      text: `${input.selection.title}${input.scopes.length == 1 ? ': ' + input.scopes[0] : ''}`,
      font: { family: "'Georgia', serif" },
      color: '#333'
    };

    if (config.type == "line") {
      this.setLineData(config.data, this.activeScopes);
    } else {
      // Bar Chart Logic
      for (let bar of input.cptWidget.bars) {
        let v = input.variables.find(n => n._id == bar['valueVarID']);
        let label = bar['label'];
        config.data.labels?.push(this.formatLabel(label));
        
        if (config.options.plugins.legend) {
            config.options.plugins.legend.display = false;
        }
        
        if (!v) continue;

        let dataPoint = parseFloat(v.data[input.scopes[0]].replace(",", ""));
        config.data.datasets[0].data.push(dataPoint);
      }
      
      // Ensure scales exist
      if (!config.options.scales) config.options.scales = {};
      
      // Update Y scale (or X if horizontal, but traditionally Y for values in v2)
      // For v4 horizontal bar (indexAxis: 'y'), the value axis is 'x'.
      // Assuming 'horizontalBar' meant values on X axis:
      if ((input.selection.type as string) === 'horizontalBar') {
          config.options.scales.x = { beginAtZero: true };
      } else {
          config.options.scales.y = { beginAtZero: true };
      }

      this.setChartColors(config);
    }

    return config;
  }

  private determineChartType(input: GeneratedEfieldsWidgetInput): "scatter" | "bar" {
    let type: "scatter" | "bar" = "bar";
    for (let trial of input.trials) {
      // Check if xAxis value is a number (scatter) or string (bar)
      if (!isNaN(Number((trial as any)[input.xAxis]))) {
        type = "scatter";
        break; // Found a number, treat as scatter
      }
    }
    return type;
  }

  private setEfieldConfigOptions(
    config: ChartConfiguration,
    input: GeneratedEfieldsWidgetInput,
    type: string
  ): ChartConfiguration {
    console.log(input);
    let options: ChartOptions = {
      responsive: true,
      plugins: {
        title: {
            display: true,
            text: "None", // Will be overwritten or logic needed?
            font: { size: 14 },
            color: '#000',
        },
        legend: {
            display: true,
            position: "right",
            labels: {
                usePointStyle: true,
            }
        }
      },
      scales: {
        x: {
          display: true,
          title: {
            display: true,
            text: "Units", // Overwritten below
            color: '#000',
          },
        },
        y: {
          display: true,
          title: {
            display: true,
            text: "Units", // Overwritten below
            color: '#000',
          },
        },
      },
    };

    // Setting labels dynamically if needed
    // options.scales.x.title.text = 'Units'; 
    // options.scales.y.title.text = 'Units';

    let trialGroups: any = {};
    for (let trial of input.trials) {
      let county = trial.county;
      if (county in trialGroups) trialGroups[county].push(trial);
      else trialGroups[county] = [trial];
    }

    config.data.datasets = [];
    config.data.labels = []; // Labels usually not used for Scatter x-axis, but kept for Bar

    for (const [key, value] of Object.entries(trialGroups)) {
      let data: any[];
      
      if (type === 'scatter') {
          data = (value as EFieldsTrial[]).map((trial) => {
            return {
                x: (trial as any)[input.xAxis], 
                y: (trial as any)[input.yAxis]
            };
          });
      } else {
          // Fallback for bar or other types if needed
           data = (value as EFieldsTrial[]).map(t => (t as any)[input.yAxis]);
      }

      let dataset: ChartDataset = {
        label: key,
        type: type as ChartType,
        data: data
      };
      config.data.datasets.push(dataset);
    }
    
    console.log(config);
    config.type = type as ChartType;
    config.options = { ...config.options, ...options };
    return config;
  }

  getGeneratedEfieldsConfig(input: GeneratedEfieldsWidgetInput): ChartConfiguration {
    let config = this.getDefaultConfig();
    this.type = this.determineChartType(input);
    config = this.setEfieldConfigOptions(config, input, this.type);
    return config;
  }

  initalizeChart(): void {
    console.log('init');
    const input = this.widgetInput;
    let config: ChartConfiguration;

    if (input.selection.source == "efields") {
      if ((input as any)["xAxis"] && (input as any)["yAxis"]) {
        config = this.getGeneratedEfieldsConfig(input as GeneratedEfieldsWidgetInput);
      } else {
        config = this.getEfieldsConfig(input as EfieldsWidgetInput);
      }
    } else {
      config = this.getCptConfig(input as CptWidgetInput);
    }

    this.data = config.data.datasets;
    this.labels = config.data.labels as string[];
    this.options = config.options || {};
    this.type = config.type;
    
    console.log(this.data);
    console.log(this.labels);
    console.log(this.options);
    console.log(this.type);
  }

  setLineData(data: ChartData, scopes: string[]): void {
    let labels: any[] = [];
    let datasets: ChartDataset[] = [];
    
    for (let s of scopes) {
      let sortedData = this.sortLineData(this.cptWidgetInput, s);
      if (!labels.length) labels = sortedData.map((yearVal) => yearVal.year);
      datasets.push({
          label: s, 
          data: sortedData.map(yearVal => yearVal.value) as number[] // Cast for type compatibility
      });
    }

    data.labels = labels;
    data.datasets = datasets;
    this.setLineChartColors(datasets);
  }

  getLineChartData(scopes: string[]): ChartDataset[] {
    let dataSets: ChartDataset[] = [];
    for (let scope of scopes) {
      let sortedDataAtScope = this.sortLineData(this.cptWidgetInput, scope);
      console.log(sortedDataAtScope);
      let dataSet: ChartDataset = {
        data: sortedDataAtScope.map((yearValue) => yearValue.value) as number[],
        label: scope
      };
      dataSets.push(dataSet);
    }
    this.setLineChartColors(dataSets);
    return dataSets;
  }

  setChartColors(config: ChartConfiguration) {
    if (config.data.datasets.length > 0) {
        config.data.datasets[0].backgroundColor = ChartBackgroundColors;
        config.data.datasets[0].borderColor = ChartBorderColors;
        config.data.datasets[0].borderWidth = 1;
    }
  }

  setLineChartColors(datasets: ChartDataset[]) {
    datasets.forEach((dataset, index) => {
      dataset.backgroundColor = ChartBackgroundColors[index % ChartBackgroundColors.length];
      dataset.borderColor = ChartBorderColors[index % ChartBorderColors.length];
      dataset.borderWidth = 1;
    });
  }

  private formatLabel(label: string): string[] {
    label = this._explorer.toTitleCase(label);
    let formattedLabel: string[] = [];
    if (label.length > 12) {
      let firstSpaceIndex = label.indexOf(" ", 11);
      if (firstSpaceIndex == -1) {
        formattedLabel.push(label);
      } else {
        let line = label.slice(0, firstSpaceIndex);
        formattedLabel = [
          line,
          ...this.formatLabel(label.slice(firstSpaceIndex + 1, label.length)),
        ];
      }
    } else {
      formattedLabel.push(label);
    }
    return formattedLabel;
  }

  getPosAndSize(): { x: number; y: number; rows: number; cols: number } {
    return {
      x: this.widgetInput.x,
      y: this.widgetInput.y,
      rows: this.widgetInput.rows ? this.widgetInput.rows : 1,
      cols: this.widgetInput.cols ? this.widgetInput.cols : 1,
    };
  }
}
